import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaShieldAlt, FaArrowRight, FaArrowLeft, FaUpload, FaSearch,
  FaCheckCircle, FaTimesCircle, FaDownload, FaTrashAlt, FaExclamationTriangle,
  FaChevronRight, FaClock, FaUsers
} from "react-icons/fa";

export default function NumberVerificationGuide() {
  useEffect(() => {
    document.title = "Number Verification Guide | Walytic - Check WhatsApp Numbers Instantly";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="Number Verification Guide" subtitle="Check whether phone numbers are active on WhatsApp before you send a single message." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'single-mode', label: 'Single number verification' },
              { id: 'bulk-mode', label: 'Bulk verification via file upload' },
              { id: 'accepted-formats', label: 'Accepted column names and formats' },
              { id: 'verification-history', label: 'Verification history table' },
              { id: 'export-and-clear', label: 'Export and clear history' },
              { id: 'tips', label: 'Tips for better results' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Single Mode */}
        <section id="single-mode" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Single number verification</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Need a quick check on one number? Use the single verification mode.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open Number Verification</h3>
                <p className="text-ink-secondary">
                  Click <strong>Number Verification</strong> in the sidebar. The page defaults to single mode.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Enter the phone number</h3>
                <p className="text-ink-secondary">
                  Type the full number with country code (e.g., <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">919876543210</code>).
                  Click <strong>Verify</strong>.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">See the result</h3>
                <p className="text-ink-secondary mb-3">
                  The result appears instantly:
                </p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <FaCheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Valid</span>
                  </div>
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                    <FaTimesCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-800">Invalid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Bulk Mode */}
        <section id="bulk-mode" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Bulk verification via file upload</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Got a list of hundreds or thousands of numbers? Switch to Bulk mode.
            Upload a CSV or Excel file and Walytic checks every number in one go.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Switch to Bulk tab</h3>
                <p className="text-ink-secondary">
                  Click the <strong>Bulk</strong> tab at the top of the Number Verification page.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Upload your file</h3>
                <p className="text-ink-secondary">
                  Drag-and-drop or click to upload a <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.csv</code> or
                  {' '}<code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">.xlsx</code> file.
                  Walytic automatically removes duplicate numbers from the list before verifying.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Wait for results</h3>
                <p className="text-ink-secondary">
                  A progress bar shows how far along the verification is. Each number is checked
                  and marked as Valid or Invalid. Results appear in the history table as they complete.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Accepted Formats */}
        <section id="accepted-formats" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Accepted column names and formats</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Walytic looks for the phone number in a column with any of these header names (case-insensitive):
          </p>

          <div className="flex flex-wrap gap-2 mb-6">
            {['phone', 'number', 'mobile', 'whatsapp', 'contact'].map((col) => (
              <code key={col} className="bg-slate-100 px-3 py-1.5 rounded text-sm font-medium text-ink-primary">{col}</code>
            ))}
          </div>

          <p className="text-ink-secondary mb-4">
            Numbers can include a leading <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">+</code>, spaces,
            dashes, or brackets. Walytic strips these automatically before checking.
          </p>

          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm font-medium text-ink-primary mb-2">All of these work:</p>
            <ul className="text-sm text-ink-secondary space-y-1">
              <li><code className="bg-white px-1.5 py-0.5 rounded">+91 98765 43210</code></li>
              <li><code className="bg-white px-1.5 py-0.5 rounded">91-9876543210</code></li>
              <li><code className="bg-white px-1.5 py-0.5 rounded">(91) 9876543210</code></li>
              <li><code className="bg-white px-1.5 py-0.5 rounded">919876543210</code></li>
            </ul>
          </div>
        </section>

        {/* Section: Verification History */}
        <section id="verification-history" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Verification history table</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Every verified number shows up in the history table with three columns:
          </p>

          <div className="bg-surface-card rounded-xl border border-surface-border overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Column</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-primary">Description</th>
                </tr>
              </thead>
              <tbody className="text-ink-secondary">
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Number</td>
                  <td className="py-3 px-4">The phone number that was checked</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Status</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1"><FaCheckCircle className="h-3.5 w-3.5 text-green-500" /> Valid</span>
                    {' '}or{' '}
                    <span className="inline-flex items-center gap-1"><FaTimesCircle className="h-3.5 w-3.5 text-red-500" /> Invalid</span>
                  </td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-3 px-4 font-medium">Verified At</td>
                  <td className="py-3 px-4">Date and time the check ran</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Export and Clear */}
        <section id="export-and-clear" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Export and clear history</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaDownload className="h-4 w-4 text-brand" />
                <h4 className="font-semibold text-sm text-ink-primary">Export as Excel</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Downloads the full verification history as a <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">.xlsx</code> file.
                Includes number, status, and timestamp for every entry.
              </p>
            </div>
            <div className="bg-surface-card border border-surface-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FaTrashAlt className="h-4 w-4 text-red-600" />
                <h4 className="font-semibold text-sm text-ink-primary">Clear History</h4>
              </div>
              <p className="text-sm text-ink-secondary">
                Wipes the verification history for the current session. This is per-session, so
                clearing one session does not affect others.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips for better results</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Verify before every campaign', text: 'Numbers go inactive over time. Run verification right before sending to get accurate results.' },
              { title: 'Use bulk for large lists', text: 'Single mode works for spot-checks. For anything over 20 numbers, upload a file instead.' },
              { title: 'Export and filter', text: 'After bulk verification, export the results and filter to "Valid" only. Use that cleaned file for your Send via Excel campaign.' },
              { title: 'Country code matters', text: 'A number without a country code cannot be verified. Always include the country prefix (91 for India, 44 for UK, 1 for US).' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev / Next */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/send-via-excel" className="text-sm text-ink-muted hover:text-ink-primary flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Send via Excel
          </Link>
          <Link to="/guide/group-export" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: Group Export <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
